import React from 'react';
import { BaseTable } from 'containers/application-manager/base-table/base-table';
import { Grid } from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import { makeStyles } from '@mui/styles';
import { IGetCustomerprofilePharmacyResponse } from 'models/application-manager/customer-profile/IGetCustomerProfilePharamacyResponse';
import { IHeaderCell } from '../../base-table/types';
import { IResponse, IResultChildren, IResult } from './types';
import { cpStyle } from '../index.styles';
import { ChildrenComponent } from './children-comp';
import { NewCustomerProfileModal } from '../modal/new-customer-profile-modal';

interface ITableProps {
  searchTerm: string | undefined;
  pharmacyList: IGetCustomerprofilePharmacyResponse[];
  userHasPermissionToEditCustomerProfileItem: boolean;
  activeAnyField: boolean;
  setReloadTrigger: (date: Date | undefined) => void;
  allCustomers: { label: string; value: number }[];
  customerId: number;
}

export const DataTable: React.FC<ITableProps> = (props: ITableProps) => {
  const useStyles = makeStyles(cpStyle);
  const classes = useStyles();

  const [addModalOpen, setAddModalOpen] = React.useState<boolean>(false);
  const [selectedParentRow, setSelectedParentRow] = React.useState<IResult | null>(null);
  const { pharmacyList, setReloadTrigger, allCustomers, customerId } = props;

  const parentPkSelector = (parent: IResult): number => parent.id;
  const childPkSelector = (child: IResultChildren): number => child.id;

  const customerProfileTableInfo = React.useMemo<IResponse | undefined>(() => {
    if (pharmacyList && pharmacyList.length) {
      const data = pharmacyList.map(item => {
        return {
          id: item.id,
          name: item.name,
          children: item.accordionInfo,
        };
      });
      return {
        totalCount: 1,
        results: data,
      };
    }
    return undefined;
  }, [pharmacyList]);

  const COLUMN_SETTINGS: IHeaderCell<IResult, IResultChildren>[] = [
    {
      label: 'Pharmacy Name',
      parentKey: 'name',
      hideLeftPadding: true,
      childColSpan: 2,
      sortable: true,
    },
  ];

  const renderCustomerProfileModal = (): JSX.Element => {
    return (
      <NewCustomerProfileModal
        open={addModalOpen}
        onCancel={() => {
          setAddModalOpen(false);
          setSelectedParentRow(null);
          setReloadTrigger(new Date());
        }}
        onSuccess={() => setReloadTrigger(new Date())}
        allCustomers={allCustomers}
        selectedParentRow={selectedParentRow}
        customerId={customerId}
      />
    );
  };

  const columnSettings = React.useMemo(() => {
    if (props.userHasPermissionToEditCustomerProfileItem) {
      return COLUMN_SETTINGS.concat({
        sortable: true,
        parentComponent: parent => {
          return (
            <>
              <div
                onClick={e => {
                  e.stopPropagation();
                  setSelectedParentRow(parent);
                  setAddModalOpen(true);
                }}
              >
                <CreateIcon />
              </div>
            </>
          );
        },
      });
    }
    return COLUMN_SETTINGS;
  }, [props.userHasPermissionToEditCustomerProfileItem]);

  const renderChildRecord = (parent: IResult) => {
    const childrenInfo =
      parent && parent.children && parent.children.length
        ? parent.children.sort((a, b) => a.order - b.order)
        : [];
    return (
      <div>
        {childrenInfo &&
          childrenInfo.length > 0 &&
          childrenInfo.map(child => {
            return (
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                className={classes.accordionheader}
              >
                <Grid item xs={12} className={classes.parentDataCell}>
                  <ChildrenComponent key={`children-${parent.id}`} childInfo={child} />
                </Grid>
              </Grid>
            );
          })}
      </div>
    );
  };

  return (
    <>
      {addModalOpen ? renderCustomerProfileModal() : null}
      <BaseTable<IResponse, IResult, IResultChildren>
        actionsPermitted={false}
        enableParentCheckboxes={false}
        enableChildCheckboxes={false}
        orderByDefaultParent="name"
        dataSet={customerProfileTableInfo}
        paginationQueryParamSettings={{
          pageSizeQueryStringKey: 'pageSize',
          pageNumberQueryStringKey: 'pageNumber',
          searchTermQueryStringKey: 'searchTerm',
          sortPropQueryStringKey: 'sortProp',
          sortOrderQueryStringKey: 'sortOrder',
        }}
        childPkSelector={childPkSelector}
        parentPkSelector={parentPkSelector}
        customChildrenComponent={(parent: IResult) => renderChildRecord(parent)}
        columnSettings={columnSettings}
        searchTerm={JSON.stringify({
          type: 'MULTIPLE',
          search: [
            {
              values: props.searchTerm,
              columns: ['name'],
              fullMatch: false,
              active: props.activeAnyField,
            },
          ],
        })}
        classesOverride={{
          lastCell: classes.lastCell,
          table: classes.wrapText,
          customChildRow: classes.childRow,
        }}
      />
    </>
  );
};
